<template>
  <section
    id="section__monthlyearlypaystatistics"
    class="view-container mr-1 ml-1"
    v-if="dailyAmount && salesDailyTotal"
  >
    <div class="width-100">
      <v-row no-gutters>
        <v-col cols="12">
          <v-sheet height="64">
            <v-toolbar flat class="d-flex justify-center">
              <v-btn fab text small color="grey darken-2" @click="prev">
                <v-icon small> mdi-chevron-left </v-icon>
              </v-btn>
              <v-toolbar-title v-if="$refs.calendar && $refs.calendar.value">
                {{ $moment($refs.calendar.value).format('YYYY년 MM월') }}
              </v-toolbar-title>
              <v-btn
                fab
                text
                small
                color="grey darken-2"
                @click="next"
                :disabled="isDisabled"
              >
                <v-icon small> mdi-chevron-right </v-icon>
              </v-btn>
            </v-toolbar>
          </v-sheet>
          <v-card flat>
            <p class="font--m-2 text-center mb-0" v-if="isSameMonth">
              총 <strong class="font--m-1 money-text-color" data-a45hs67>{{
                dailyTotal | commaFormat
              }}</strong>
              원
            </p>
            <p class="font--m-2 text-center mb-0" v-else>
              <strong class="font--m-1 money-text-color" data-a45hs67>0</strong>
              원
            </p>
          </v-card>
          <v-sheet height="530">
            <v-calendar
              ref="calendar"
              v-model="focus"
              :events="events"
              @click:date="goToDay"
              @click:event="showEvent"
              @change="updateRange"
              :event-color="''"
              :event-text-color="getEventColor"
              :day-header-height="20"
            ></v-calendar>
          </v-sheet>
        </v-col>
        <v-col cols="12">
          <div class="font-size--12">
            <span>단위: 원</span>
          </div>
        </v-col>
      </v-row>
    </div>
  </section>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  data() {
    return {
      dailyAmount: null,
      salesDailyTotal: null,
      year: null,
      month: null,
      selectedDate: null,
      yearMonth: null,
      focus: '',
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      eventsTemp: [],
    }
  },
  computed: {
    isSameMonth() {
      const yearAndMonth = this.selectedDate.slice(0, 7)
      const yearMonth1 = this.$moment(yearAndMonth, 'YYYY-MM').format('YYYY-MM')
      const dailyAmount = this.dailyAmount[0]?.date.slice(0, 7)
      const yearMonth2 = this.$moment(dailyAmount, 'YYYY-MM').format('YYYY-MM')
      return yearMonth1 === yearMonth2
    },
    dailyAmountTotal() {
      const sum = this.dailyAmount.reduce((acc, curr) => acc + curr.amount, 0)
      return sum
    },
    dailyInterestFee() {
      const interest = this.dailyAmount.reduce((acc, curr) => acc + curr.interest, 0)
      const fee = this.dailyAmount.reduce((acc, curr) => acc + curr.fee, 0)
      return interest + fee
    },
    dailyTotal() {
      if (!this.salesDailyTotal) return 0
      return this.salesDailyTotal.reduce((acc, curr) => acc + curr.total, 0)
    },
    dailySalesCount() {
      return this.salesDailyTotal ? this.salesDailyTotal.length : 0
    },
    dailyExpectedFee() {
      if (!this.salesDailyTotal) return 0
      return this.salesDailyTotal.reduce((acc, curr) => acc + curr.expectedFee, 0)
    },
    cardTotal() {
      if (!this.salesDailyTotal) return 0
      
      return this.salesDailyTotal
        .filter(sale => {
          const cardSources = ['nice', 'cfa']
          return cardSources.includes(sale.source)
        })
        .reduce((acc, curr) => acc + curr.total, 0)
    },
    deliveryTotal() {
      if (!this.salesDailyTotal) return 0

      return this.salesDailyTotal
        .filter(sale => {
          const deliverySources = ['baemin', 'yogiyo', 'coupang']
          return deliverySources.includes(sale.source)
        })
        .reduce((acc, curr) => acc + curr.total, 0)
    },
    cardSalesCount() {
      if (!this.salesDailyTotal) return 0

      return this.salesDailyTotal
        .filter(sale => ['nice', 'cfa'].includes(sale.source))
        .reduce((acc, curr) => acc + curr.count, 0)
    },
    deliverySalesCount() {
      if (!this.salesDailyTotal) return 0

      return this.salesDailyTotal
        .filter(sale => ['baemin', 'yogiyo', 'coupang'].includes(sale.source))
        .reduce((acc, curr) => acc + curr.count, 0)
    },
    events() {
      if (!this.eventsTemp) return []

      return this.eventsTemp.map((ev) => ({
        name: `${this.changeMoney(ev.amount)}`,
        start: ev.date,
        end: ev.date,
        textColor: '#3c73dd',
        timed: false,
        origin: ev,
      }))
    },
    isDisabled() {
      const nextMonth = this.$moment(this.focus).add(1, 'month')
      const today = this.$moment().startOf('month')
      return nextMonth.isAfter(today, 'month')
    },
  },
  async created() {
    const { year, month } = this.getYearAndMonthFromSelectedDate()
    this.year = year
    this.month = month
    this.dailyAmount = await this.getDailyAmount({ year: this.year, month: this.month })
    this.salesDailyTotal = await this.getSalesDailyTotal({ year: this.year, month: this.month })
    this.yearMonth = this.$moment(this.selectedDate, 'YYYY-MM').format('YYYY년 MM월')
    this.focus = this.selectedDate
  },
  updated() {
    this.$nextTick(() => {
      if (this.$refs.calendar) {
        this.$refs.calendar.checkChange()
      }
    })
  },
  methods: {
    ...mapActions('earlyPayment', ['getDailyAmount', 'getSalesDailyTotal']),
    getYearAndMonthFromSelectedDate() {
      this.selectedDate = this.$route.query.date
      const dateObj = new Date(this.selectedDate)
      const year = dateObj.getFullYear()
      const month = dateObj.getMonth() + 1
      return { year, month }
    },
    viewDay({ date }) {
      this.focus = date
      this.type = 'day'
    },
    getEventColor(event) {
      return event.origin.type === 'card' ? '#3c73dd' : '#fe8100'
    },
    setToday() {
      this.focus = ''
    },
    prev() {
      this.$router.replace({
        path: '/earlypay/statistics/month-sales',
        query: { date: this.convert(this.$moment(this.focus).add(-1, 'month')) },
      })
    },
    next() {
      this.$router.replace({
        path: '/earlypay/statistics/month-sales',
        query: { date: this.convert(this.$moment(this.focus).add(+1, 'month')) },
      })
    },
    convert(date) {
      return date.format('YYYY-MM-DD')
    },
    async getData({ start, end }) {
      if (!this.salesDailyTotal) return

      this.eventsTemp = this.salesDailyTotal.map(sale => ({
        date: sale.date,
        amount: sale.total,
        expectedFee: sale.expectedFee
      }))
    },
    showEvent({ nativeEvent, event }) {
      const eventData = event.origin
      this.$router.push({ 
        name: 'sales', 
        query: { date: eventData.date } 
      })
      nativeEvent.stopPropagation()
    },
    async updateRange({ start, end }) {
      if (!this.salesDailyTotal) return
      await this.getData({ start: start.date, end: end.date })
    },
    goToDay({ date }) {
      const selectedDate = this.$moment(date)
      const today = this.$moment().startOf('day')
      if (selectedDate.isAfter(today)) {
        return
      }
      this.focus = date
      this.type = 'day'
      this.$router.push({ 
        name: 'sales', 
        query: { date } 
      })
    },
    changeMoney(won) {
      const arrWon = [
        {
          range: 100000000,
          toFixed: 1,
          text: '억',
        },
        {
          range: 10000,
          toFixed: 0,
          text: '만',
        },
        {
          range: 1,
          toFixed: 0,
          text: '원',
        },
      ]
      const r = [arrWon.find((v) => won >= v.range)].map((v) => {
        if (!v) {
          return ''
        }
        const result = won / v.range
        const fixedNumber = Number(result.toFixed(v.toFixed)).toLocaleString()
        return `${fixedNumber}${v.text}`
      })[0]
      return r
    },
  },
}
</script>
<style lang="scss" scoped>
#section__monthlyearlypaystatistics {
  .v-card {
    width: 100%;
    border-radius: 10px;

    .top-table {
      width: 100%;

      tr {
        td {
          width: 50%;
          padding-bottom: 4px;
          &:first-child {
            width: 50%;
            padding-right: 16px;
          }
        }

        .text-amount {
          text-align: right;
        }
      }
    }
  }
}
.money-text-color[data-a45hs67] {
  color: #3c73dd;
}
.early-text-color[data-c98hs75] {
  color: #fe8100;
}
.card-text-color {
  color: #3c73dd;
}
.delivery-text-color {
  color: #fe8100;
}
</style>
